class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always' ||
    this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};

    // Create debounced version of setHeaderHeight
    this.debouncedSetHeaderHeight = this.debounce(this.setHeaderHeight.bind(this), 100);

    this.setHeaderHeight();

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.debouncedSetHeaderHeight);

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.debouncedHandleStickyHeader = this.debounce(this.handleStickyHeader.bind(this), 66);
    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  // On scroll handler
  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    this.debouncedHandleStickyHeader(scrollTop);
    this.debouncedSetHeaderHeight();
  }

  // Handle sticky header based on scroll direction and position
  handleStickyHeader(scrollTop) {

    // Don't hide header if predictive search is open
    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    // Scrolling down and past the header
    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));

    // Scrolling up and past the header
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');

      // Prevent revealing the header if the user quickly scrolls up and down
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));

      // Debounce the reveal of the header to prevent it from flashing when the user scrolls quickly
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }

    // Scrolling back to the top
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    // Update current scroll position
    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchForm = document.querySelector('.header__search-form-wrapper');
    this.searchForm.classList.remove('is-active');
  }
}

customElements.define('sticky-header', StickyHeader);

class OpenSearch extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.searchButton = this.querySelector('button');
    this.searchForm = document.querySelector('.header__search-form-wrapper');

    this._toggleSearchHandler = this.toggleSearch.bind(this);
    this.searchButton.addEventListener('click', this._toggleSearchHandler);

    this.mainContent = document.getElementById('MainContent');
    if (this.mainContent) {
      this._onMainClick = (e) => {
        if (!this.searchForm) return;
        if (!this.searchForm.classList.contains('is-active')) return;
        this.searchForm.classList.remove('is-active');
      };

      this.mainContent.addEventListener('click', this._onMainClick);
    }
  }

  disconnectedCallback() {
    if (this.searchButton && this._toggleSearchHandler) this.searchButton.removeEventListener('click', this._toggleSearchHandler);
    if (this.mainContent && this._onMainClick) this.mainContent.removeEventListener('click', this._onMainClick);
  }

  toggleSearch() {
    this.searchForm.classList.toggle('is-active');
  }
}

customElements.define('open-search', OpenSearch);
